import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertMessageIcon from "@tCentro/Icons/AlertMessageIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    container?: GridContainerProps;

    subCategoryItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLink?: LinkPresentationProps;
    mobileDefaultIconWrapper?: InjectableCss;
    cardWrapper?: InjectableCss;
    subCategoryNameWrapper?: InjectableCss;
    mobileCloseIconWrapper?: InjectableCss;
    hoveredLink?: InjectableCss;
    subcategorydescriptionWrapper?: InjectableCss;
    defaultProductImage?: InjectableCss;
    subCategoryContent?: InjectableCss;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    subcategorydescriptionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: 8px;
            @media (min-width: 992px) and (max-width: 1200px) {
                text-align: left;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        text-align: left;
                    `,
                    css`
                        text-align: left;
                    `,
                    css`
                        text-align: left;
                    `,
                    null,
                ])}
        `,
    },

    hoveredLink: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: 16px;
            color: ${COLORS.link.base};
        `,
    },
    cardWrapper: {
        css: css`
            width: 100%;
        `,
    },
    subCategoryNameWrapper: {
        css: css`
            width: 100%;
            padding: 32px;
            background: ${COLORS.background.grayShade};
            width: 100%;
            padding: 32px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            min-height: 240px;
        `,
    },
    container: {
        gap: 0,
        css: css`
            > div[class^="GridOffset"] {
                justify-content: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            justify-content: flex-start;
                        `,
                        css`
                            justify-content: flex-start;
                        `,
                        css`
                            justify-content: flex-start;
                        `,
                        null,
                    ])}
            }
            margin-bottom: 104px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 78px;
                    `,
                    null,
                    null,
                ])}
        `,
    },

    subCategoryItem: {
        width: [12, 12, 6, 3, 3],
        css: css`
            padding: 0 12px;
            margin-top: 20px !important;
            min-height: 403px;
            > div {
                background: ${COLORS.background.grayShade};
                height: 100%;
                width: 100%;
            }

            @media (min-width: 992px) and (max-width: 1200px) {
                flex-basis: 33% !important;
                max-width: 33% !important;
            }

            @media (min-width: 768px) and (max-width: 991px) {
                &:nth-child(n + 4) {
                    margin-top: 24px !important;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                        margin-bottom: 24px !important;
                    `,
                    css`
                        padding: 0;
                        margin-bottom: 24px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    subCategoryImageClickable: {
        css: css`
            width: 100%;

            > div {
                width: 100%;
            }
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: auto;
                max-height: 163px;
                width: 100%;
                aspect-ratio: 1 / 1;
                object-fit: cover;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        css`
                            object-fit: initial;
                            max-height: initial;
                            aspect-ratio: 2/1;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
        `,
    },
    subCategoryNameLink: {
        css: css`
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            size: TYPOGRAPHY.size.tiny,
            weight: TYPOGRAPHY.weight.mediumbold,
            css: css`
                line-height: 24px;
                width: 100%;
                word-wrap: break-word;
                color: ${getProp("theme.typography.p.color")} !important;
                font-size: ${TYPOGRAPHY.size.large};
            `,
        },
    },

    defaultProductImage: {
        css: css`
            [class^="LazyImageStyle-sc"] {
                width: 100%;
            }
            background: ${COLORS.white};
            justify-content: center;
            img {
                width: 100%;
                max-height: 163px;
                object-fit: cover;
                height: auto;
                aspect-ratio: 1/1;
            }
        `,
    },
    subCategoryContent: {
        css: css`
            margin-bottom: 16px;
            p {
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                font-family: "Poppins" !important;
                color: ${getProp("theme.typography.p.color")};
            }
            html body {
                background: transparent;
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                font-family: "Poppins" !important;
                color: ${getProp("theme.typography.p.color")};
                p {
                    font-size: ${TYPOGRAPHY.size.smaller} !important;
                    font-weight: ${TYPOGRAPHY.weight.base};
                    line-height: ${TYPOGRAPHY.lineHeight.base};
                    font-family: "Poppins" !important;
                    color: ${getProp("theme.typography.p.color")};
                }
            }
        `,
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CENCategoryDetailsSubCategories = ({ category, fields: { showImages } }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <>
            <GridContainer {...styles.container}>
                {category.subCategoryIds.map(subCategoryId => (
                    <GridItem key={subCategoryId} {...styles.subCategoryItem} id={`mobileContainer${subCategoryId}`}>
                        <SubCategoryLink categoryId={subCategoryId} showImages={showImages} />
                    </GridItem>
                ))}
            </GridContainer>
        </>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    if (!category) {
        return null;
    }

    return (
        <>
            <Link href={category.path}>
                <StyledWrapper {...styles.cardWrapper}>
                    {showImages && category.smallImagePath ? (
                        <Clickable href={category.path} {...styles.subCategoryImageClickable}>
                            <LazyImage
                                src={category?.largeImagePath ? category?.largeImagePath : category?.smallImagePath}
                                altText={category.imageAltText}
                                {...styles.subCategoryImage}
                            />
                        </Clickable>
                    ) : (
                        <Clickable
                            href={category.path}
                            data-test-selector="productImage"
                            {...styles.defaultProductImage}
                        >
                            <LazyImage
                                src="/userFiles/images/default-product-image.png"
                                altText="Default product image"
                            />
                        </Clickable>
                    )}

                    <StyledWrapper {...styles.subCategoryNameWrapper}>
                        <Link
                            href={category.path}
                            {...styles.subCategoryNameLink}
                            data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                        >
                            {category.shortDescription}
                        </Link>
                        <StyledWrapper {...styles.subcategorydescriptionWrapper}>
                            {category.htmlContent && (
                                <StyledWrapper {...styles.subCategoryContent}>
                                    {parse(
                                        transformContentHtml(truncate(category.htmlContent, { length: 110 })),
                                        parserOptions,
                                    )}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        <Link
                            href={category.path}
                            {...styles.hoveredLink}
                            data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                        >
                            {translate("Shop Products")}
                        </Link>
                    </StyledWrapper>
                </StyledWrapper>
            </Link>
        </>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CENCategoryDetailsSubCategories),
    definition: {
        group: "Basic",
        icon: "list-ul",
        displayName: "Product Category Tile",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
